import { AUTH_LEARNER_CREATED } from "@outschool/auth-shared";
import { ChildFragmentFragment } from "@outschool/gql-frontend-generated";
import { useLocalStorageState } from "@outschool/local-storage";
import { useTranslation } from "@outschool/localization";
import { useSession } from "@outschool/ui-auth";
import { useIsMobile } from "@outschool/ui-utils";
import Cookies from "js-cookie";
import React, {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
} from "react";

import { FilterOption } from "../components/search/SearchBoxFilterButton";
import { useActiveLearner } from "./ActiveLearnerProvider";

const LOCAL_STORAGE_KEY = "searchByLearnerUid";

interface SearchByLearnerContextValue {
  showLearnerFilter: boolean;
  learner: ChildFragmentFragment | null;
  setLearner: (learner: ChildFragmentFragment | null) => void;
}

const SearchByLearnerContext = createContext<SearchByLearnerContextValue>({
  showLearnerFilter: false,
  learner: null,
  setLearner: () => {},
});

interface SearchByLearnerProviderProps {
  children: ReactNode;
}

// We use a sentinel value to indicate that the learner has not been set, so we can distinguish between
// the case where the learner has been set to null and the case where the learner has not been set at all.
// This means that "null" indicates that any/all learners are selected.
// This is useful, for example, when the user makes a search from the global search bar with "All learners" selected.
// In that case, we don’t want to default them to a learner because they’ve explicitly selected "All learners".
const SENTINEL_VALUE = "__SENTINEL__";
function learnerSet(learnerSearchValue: string | null) {
  if (learnerSearchValue === SENTINEL_VALUE) {
    return false;
  }
  return true;
}

export function SearchByLearnerProvider({
  children,
}: SearchByLearnerProviderProps) {
  const { setActiveLearner } = useActiveLearner();
  const [learnerUid, saveLearnerUid] = useLocalStorageState<string | null>(
    LOCAL_STORAGE_KEY,
    SENTINEL_VALUE,
  );
  const { currentUser } = useSession();

  const showLearnerFilter = Boolean(currentUser?.children?.length);

  const learner = React.useMemo(
    () =>
      currentUser?.children.find(child => child?.uid === learnerUid) ?? null,
    [currentUser?.children, learnerUid],
  );

  const setLearner = useCallback(
    (learner: ChildFragmentFragment | null) => {
      saveLearnerUid(learner?.uid ?? null);
    },
    [saveLearnerUid],
  );

  // Initialize learner selection based on different scenarios
  const initializeLearner = useCallback(() => {
    // Priority 1: Learner just created during signup/onboarding
    const isLearnerCreated = Cookies.get(AUTH_LEARNER_CREATED);
    if (isLearnerCreated) {
      const createdLearner = currentUser?.children.at(0);
      if (createdLearner) {
        setLearner(createdLearner);
        setActiveLearner(createdLearner.uid);
      }
      Cookies.remove(AUTH_LEARNER_CREATED);
      return;
    }

    // Priority 2: Auto-select last created child
    if (currentUser && !learnerSet(learnerUid)) {
      const lastCreatedChild = currentUser.children.at(-1);
      if (lastCreatedChild) {
        setLearner(lastCreatedChild);
      }
    }
    // We have to ignore updates to the learner here, otherwise we’ll continually initialize the learner if the user
    // tries to set the learner to null, e.g.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser?.children, setLearner, setActiveLearner]);

  useEffect(() => {
    initializeLearner();
  }, [initializeLearner]);

  return (
    <SearchByLearnerContext.Provider
      value={{
        showLearnerFilter,
        learner,
        setLearner,
      }}
    >
      {children}
    </SearchByLearnerContext.Provider>
  );
}

export function useSearchByLearner({
  onFilterChange,
}: {
  onFilterChange?: (learner: ChildFragmentFragment | null) => void;
} = {}) {
  const { currentUser } = useSession();
  const { t } = useTranslation("client\\providers\\SearchByLearnerProvider");
  const isMobile = useIsMobile();
  const context = useContext(SearchByLearnerContext);
  const onFilterChangeRef = React.useRef(onFilterChange);
  const initializedRef = React.useRef(false);

  if (context === undefined) {
    throw new Error(
      "useSearchByLearner must be used within an SearchByLearnerProvider",
    );
  }

  const { learner, showLearnerFilter, setLearner } = context;

  useEffect(() => {
    onFilterChangeRef.current = onFilterChange;
  }, [onFilterChange]);

  useEffect(() => {
    if (!initializedRef.current) {
      initializedRef.current = true;
      return;
    }

    if (onFilterChangeRef.current) {
      onFilterChangeRef.current(learner);
    }
  }, [learner]);

  const getSearchBarPlaceholder = (learner: ChildFragmentFragment | null) => {
    if (currentUser && currentUser?.children?.length > 0 && learner) {
      if (learner.name) {
        if (isMobile) {
          return t("Find {{learnerName}}’s newest interest", {
            learnerName: learner.name,
          });
        }
        return t("Find {{learnerName}}’s newest interest", {
          learnerName: learner.name,
        });
      } else {
        return t("What’s your child’s interest?");
      }
    } else {
      if (!currentUser || isMobile) {
        return t("What’s your child’s interest?");
      }
      return t("What’s your child’s interest?");
    }
  };

  const searchBarPlaceholder = getSearchBarPlaceholder(learner);

  const allClassesOption = {
    label: t("All learners"),
    value: "",
  };

  const learnerFilterOptions = [
    allClassesOption,
    ...(currentUser?.children?.map(child => ({
      label: child.name,
      value: child.uid,
    })) || []),
  ];

  const learnerFilter = showLearnerFilter
    ? {
        options: learnerFilterOptions,
        selectedOption: learner
          ? {
              label: learner.name || "",
              value: learner.uid,
            }
          : allClassesOption,
        label: learner ? learner.name : allClassesOption.label,
        onChange: (option: FilterOption | null) => {
          const selectedChild = currentUser?.children.find(
            child => child.uid === option?.value,
          );
          setLearner(selectedChild || null);
        },
      }
    : undefined;

  return {
    ...context,
    searchBarPlaceholder,
    getSearchBarPlaceholder,
    learnerFilter,
  };
}
